import { from, of } from 'rxjs';
import { last, mergeScan } from 'rxjs/operators';
/**
 * ProcessOperators class manages a collection of process operators
 * and provides methods to add, set, get, and process these operators.
 * It implements the IProcessOperators interface for type T.
 *
 * @template T The type of data that the process operators work with
 */
export class ProcessOperators {
    /**
     * A record of process operators keyed by a string.
     * This property is used to store and manage the collection of process operators
     * that are used by the `ProcessOperators` class.
     */
    _operators;
    /**
     * Accessor for the collection of process operators.
     * @returns The record of process operators.
     */
    get operators() {
        return this._operators;
    }
    /**
     * Constructs a new instance of the ProcessOperators class.
     * @param operators - An optional object containing process operators.
     *                    It can be either an instance of IProcessOperators<T> or a record of string keys and ProcessOperator<T> values.
     */
    constructor(operators) {
        // accept either a raw operators record or another IProcessOperators instance to clone from
        if (operators && 'operators' in operators) {
            this._operators = { ...operators.operators };
        }
        else {
            this._operators = operators ?? {};
        }
    }
    /**
     * Adds a new operator to the collection.
     * @param key The key under which the operator is stored.
     * @param operator The operator to be added.
     * @returns The instance of ProcessOperators for chaining.
     * @throws Error if an operator with the same key already exists.
     */
    add(key, operator) {
        // guard against silently overwriting an existing operator under the same key
        if (Object.keys(this._operators).includes(key))
            throw Error(`Operator [${key}] already defined`);
        return this.set(key, operator);
    }
    /**
     * Sets or updates an operator in the collection.
     * @param key The key under which the operator is stored.
     * @param operator The operator to be set.
     * @returns The instance of ProcessOperators for chaining.
     */
    set(key, operator) {
        this._operators[key] = operator;
        return this;
    }
    /**
     * Removes an operator from the collection by its key.
     *
     * @param key - The key of the operator to remove.
     * @returns The current instance of `ProcessOperators` for method chaining.
     */
    remove(key) {
        delete this._operators[key];
        return this;
    }
    /**
     * Retrieves an operator from the collection by its key.
     * @param key The key of the operator to retrieve.
     * @returns The retrieved operator.
     */
    get(key) {
        return this._operators[key];
    }
    /**
     * Processes an input request through the chain of operators.
     * @param request The request to be processed.
     * @returns An Observable of the processed request.
     */
    process(request) {
        const operators = Object.values(this._operators);
        /** if no operators registered, just return the observable value */
        if (!operators.length) {
            return of(request);
        }
        // feed the request through each operator sequentially, keeping the previous value when one returns void
        return from(Object.values(this._operators)).pipe(mergeScan(
        // resolve current operator and return result or previous if void
        (value, operator) => Promise.resolve(operator(value)).then((x) => x ?? value), 
        // initial value
        request, 
        // only allow concurrency of one operator
        1), 
        // output result of last operator
        last());
    }
}
//# sourceMappingURL=ProcessOperators.js.map